import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Button, Stack, Alert, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton,
  DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Checkbox,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import { apiUrl, handleApiError } from '../utils/api';
import AppDialog from '../components/AppDialog';
import { useConfirm } from '../components/ConfirmProvider';
import { useNotify } from '../components/NotificationProvider';

/**
 * Reusable Master CRUD screen.
 *
 * Props:
 *   title:      string                  — page heading
 *   endpoint:   string                  — e.g. '/api/master/drivers'
 *   columns:    [{ key, label, render?, lookup? }]
 *               lookup: form-field key whose options map this cell's id → label
 *   formFields: [{ key, label, type?, required?, helperText?,
 *                  options?, optionsEndpoint?, optionValue?, optionLabel? }]
 *               type 'select' renders a dropdown. Options come from `options`
 *               (static [{value,label}]) or `optionsEndpoint` (fetched; mapped via
 *               optionValue|'id' and optionLabel|'name').
 *   filters:    [{ key, label }]        — optional dropdown filters. `key` must name a 'select'
 *               formField; its already-loaded options become the filter's choices, so a filter costs
 *               one line of config and no extra fetch. Filtering is client-side (masters are small).
 *   searchKeys: [string]                — optional keys the search box matches. Defaults to every
 *               column. Looked-up labels (e.g. company NAME behind companyId) are always searched.
 *   idKey:      string (default 'id')
 */
export default function MasterCrud({
  title, endpoint, columns, formFields, filters = [], searchKeys = [], idKey = 'id',
}) {
  const [filterValues, setFilterValues] = useState({});
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null => create
  const [form, setForm] = useState({});
  const [optionsMap, setOptionsMap] = useState({}); // fieldKey -> [{ value, label }]
  const [selected, setSelected] = useState([]); // ids checked for bulk delete
  const confirm = useConfirm();
  const notify = useNotify();

  const singular = title.replace(/s$/, '');
  const url = `${apiUrl}${endpoint}`;

  const load = async () => {
    setLoading(true);
    setError('');
    setInfo('');
    try {
      const response = await axios.get(url);
      const data = Array.isArray(response.data) ? response.data : (response.data?.items || []);
      setRows(data);
      setSelected([]);
      if (data.length === 0) setInfo('No matching records found.');
    } catch (err) {
      setError(handleApiError(err, 'Failed to load'));
    } finally {
      setLoading(false);
    }
  };

  // Load dropdown options (static or fetched) for any select fields.
  const loadOptions = async () => {
    const next = {};
    await Promise.all(formFields.map(async (f) => {
      if (Array.isArray(f.options)) {
        next[f.key] = f.options;
      } else if (f.optionsEndpoint) {
        try {
          const res = await axios.get(`${apiUrl}${f.optionsEndpoint}`);
          const items = Array.isArray(res.data) ? res.data : (res.data?.items || []);
          next[f.key] = items.map((it) => ({
            value: it[f.optionValue || 'id'],
            label: it[f.optionLabel || 'name'],
          }));
        } catch (err) {
          next[f.key] = [];
        }
      }
    }));
    setOptionsMap(next);
  };

  useEffect(() => { load(); loadOptions(); /* eslint-disable-next-line */ }, [endpoint]);

  const labelFor = (fieldKey, value) => {
    const opts = optionsMap[fieldKey];
    if (!opts) return value ?? '';
    const hit = opts.find((o) => String(o.value) === String(value));
    return hit ? hit.label : (value ?? '');
  };

  const openCreate = () => {
    setEditing(null);
    const init = {};
    formFields.forEach((f) => { init[f.key] = ''; });
    setForm(init);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    const init = {};
    formFields.forEach((f) => { init[f.key] = row[f.key] ?? ''; });
    setForm(init);
    setDialogOpen(true);
  };

  const handleChange = (key) => (e) => {
    setForm({ ...form, [key]: e.target.value });
  };

  const validate = () => {
    for (const f of formFields) {
      if (f.required && (form[f.key] === '' || form[f.key] == null)) {
        setError(`${f.label} is required`);
        return false;
      }
    }
    return true;
  };

  const save = async () => {
    if (!validate()) return;
    try {
      if (editing) {
        await axios.put(`${url}/${editing[idKey]}`, form);
      } else {
        await axios.post(url, form);
      }
      setDialogOpen(false);
      notify.success(`${singular} ${editing ? 'updated' : 'created'}`);
      load();
    } catch (err) {
      setError(handleApiError(err, 'Save failed'));
    }
  };

  const remove = async (row) => {
    const ok = await confirm({
      title: `Delete ${singular.toLowerCase()}`,
      message: `Delete this ${singular.toLowerCase()}? This cannot be undone.`,
      confirmText: 'Delete',
      severity: 'error',
    });
    if (!ok) return;
    try {
      await axios.delete(`${url}/${row[idKey]}`);
      notify.success(`${singular} deleted`);
      load();
    } catch (err) {
      const msg = handleApiError(err, 'Delete failed');
      setError(msg);
      notify.error(msg);
    }
  };

  // Rows after the dropdown filters + search box. Client-side: masters are small, and filtering
  // server-side would need a query param per master.
  const visibleRows = rows.filter((r) => {
    for (const f of filters) {
      const chosen = filterValues[f.key];
      if (chosen !== undefined && chosen !== '' && String(r[f.key]) !== String(chosen)) {
        return false;
      }
    }
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const keys = searchKeys.length ? searchKeys : columns.map((c) => c.key);
    if (keys.some((k) => String(r[k] ?? '').toLowerCase().includes(q))) return true;
    // Also match what the user can SEE: a lookup column shows the company name, not its id.
    return columns.some((c) => c.lookup
      && String(labelFor(c.lookup, r[c.key]) ?? '').toLowerCase().includes(q));
  });

  const clearFilters = () => {
    setFilterValues({});
    setSearch('');
  };
  const filtering = search.trim() !== ''
    || Object.values(filterValues).some((v) => v !== undefined && v !== '');

  // Selection tracks what's VISIBLE — "select all" while filtered must not silently arm a bulk
  // delete of rows scrolled out of view by a filter.
  const visibleIds = visibleRows.map((r) => r[idKey]);
  const selectedVisible = selected.filter((id) => visibleIds.includes(id));
  const allSelected = visibleRows.length > 0 && selectedVisible.length === visibleRows.length;
  const someSelected = selectedVisible.length > 0 && selectedVisible.length < visibleRows.length;

  const toggleAll = () => {
    setSelected(allSelected ? [] : visibleIds);
  };

  const toggleOne = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const bulkRemove = async () => {
    if (selected.length === 0) return;
    const ok = await confirm({
      title: `Delete ${selected.length} ${title.toLowerCase()}`,
      message: `Delete ${selected.length} selected ${title.toLowerCase()}? This cannot be undone.`,
      confirmText: `Delete ${selected.length}`,
      severity: 'error',
    });
    if (!ok) return;
    setError('');
    const count = selected.length;
    const results = await Promise.allSettled(selected.map((id) => axios.delete(`${url}/${id}`)));
    const failed = results.filter((r) => r.status === 'rejected').length;
    if (failed > 0) {
      const msg = `${failed} of ${count} could not be deleted (they may be referenced elsewhere).`;
      setError(msg);
      notify.warning(msg);
    } else {
      notify.success(`${count} ${title.toLowerCase()} deleted`);
    }
    load();
  };

  const renderCell = (c, r) => {
    if (c.render) return c.render(r);
    if (c.lookup) return labelFor(c.lookup, r[c.key]);
    return r[c.key] ?? '';
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1280, mx: 'auto' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5">
          {title} ({filtering ? `${visibleRows.length} of ${rows.length}` : rows.length})
        </Typography>
        <Stack direction="row" spacing={1}>
          {selected.length > 0 && (
            <Button variant="outlined" color="error" startIcon={<DeleteSweepIcon />} onClick={bulkRemove}>
              Delete selected ({selected.length})
            </Button>
          )}
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Add</Button>
        </Stack>
      </Stack>

      {(filters.length > 0 || rows.length > 0) && (
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <TextField
            size="small"
            placeholder={`Search ${title.toLowerCase()}…`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 220 }}
          />
          {filters.map((f) => (
            <TextField
              key={f.key}
              size="small"
              select
              label={f.label}
              value={filterValues[f.key] ?? ''}
              onChange={(e) => setFilterValues((p) => ({ ...p, [f.key]: e.target.value }))}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value=""><em>All</em></MenuItem>
              {(optionsMap[f.key] || []).map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>
          ))}
          {filtering && <Button size="small" onClick={clearFilters}>Clear</Button>}
        </Stack>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {info && rows.length === 0 && <Alert severity="info" sx={{ mb: 2 }}>{info}</Alert>}
      {loading && <CircularProgress />}

      {rows.length > 0 && visibleRows.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No {title.toLowerCase()} match this filter.{' '}
          <Button size="small" onClick={clearFilters}>Clear</Button>
        </Alert>
      )}

      {visibleRows.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={toggleAll}
                  />
                </TableCell>
                {columns.map((c) => <TableCell key={c.key}>{c.label}</TableCell>)}
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((r) => (
                <TableRow key={r[idKey]} selected={selected.includes(r[idKey])} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selected.includes(r[idKey])}
                      onChange={() => toggleOne(r[idKey])}
                    />
                  </TableCell>
                  {columns.map((c) => (
                    <TableCell key={c.key}>
                      {renderCell(c, r)}
                    </TableCell>
                  ))}
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => openEdit(r)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => remove(r)}><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <AppDialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? 'Edit' : 'Add'} {title.replace(/s$/, '')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {formFields.map((f) => (
              <TextField
                key={f.key}
                label={f.label}
                type={f.type === 'select' ? 'text' : (f.type || 'text')}
                select={f.type === 'select'}
                value={form[f.key] ?? ''}
                onChange={handleChange(f.key)}
                required={f.required}
                helperText={f.helperText}
                InputLabelProps={f.type === 'date' ? { shrink: true } : undefined}
                fullWidth
              >
                {f.type === 'select' && [
                  <MenuItem key="__none" value=""><em>None</em></MenuItem>,
                  ...(optionsMap[f.key] || []).map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  )),
                ]}
              </TextField>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save}>Save</Button>
        </DialogActions>
      </AppDialog>
    </Box>
  );
}
